<script setup lang="ts">
/**
 * MY Annual Return PreSet Panel (M32115)
 *   단일 문서 DCR (Lodgement of Annual Return) 변수 입력 + AR Report (section68 + bo-sm) 자동 첨부.
 *   흐름: Entity (chat 에서 set) → AR SUBMITTED 리스트(panel selector) → DCR 폼 → Create Task.
 *
 *   ※ Person Lookup steps 미사용 — DCR Signers 는 PreSet 에서 입력 안 함 (사용자 후속 선택).
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import PreSetDateField from './PreSetDateField.vue';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  buildAnnualReturnVariables,
  detectAnnualReturnDocFlags,
} from '../mappers/annualReturnMapper';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';

interface PersonLookupContext {
  fieldGroup: 'signers';
  defaultRole?: 'Director' | 'Secretary' | 'CEO' | 'Contact' | null;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: PersonLookupContext | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update Task' + create→update 분기.
const editMode = computed(() => !!props.editTaskId);
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true;
  if (editBaseline.value == null) return false;
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: PersonLookupContext): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
}>();

void props.selectedPerson;
void props.activePersonContext;
void props.selectedAddress;
void props.activeAddressContext;

const router = useRouter();

// ── Entity ──
const icpCd = computed(() => (props.entityData?.icpCd || props.entityData?.entityCd || '') as string);
const entityName = computed(() => (props.entityData?.entityName || props.entityData?.companyName || '') as string);
const regNo = ref('');
const regNoOld = ref('');
const constitutionClauseNo = ref('');

watch(
  () => (props.entityData?.registrationNo || '') as string,
  (v) => { if (v && !regNo.value) regNo.value = v; },
  { immediate: true }
);

// Entity 자동 정보 채움 (regNo / constitutionClauseNo / 조항 옵션).
//   1) /api/setupEntity/changeAddress/address-info/:cd → regNo + 단일 clauseNo 자동 채움
//   2) /api/entity_my/profile/:cd → resolution.articleNoOptions (L_ARTICLE_NO 마스터 후보) — combobox items.
const entityInfoLoading = ref(false);
const articleNoOptions = ref<string[]>([]);

// Governing Document Type — [[if constitution]] / [[if articles]] / [[if third_schedule]] 분기 (AOD 동일).
type GovType = 'constitution' | 'articles' | 'third_schedule';
const governingDocumentType = ref<GovType>('constitution');

/** 등록된 clause 옵션 문자열이 어떤 Governing Document 종류인지 분류. */
function clauseOptionType(opt: string): GovType | 'other' {
  const s = String(opt || '');
  // 우선순위: Third Schedule > Articles(Article(s)·구 Table A·Regulation·1965·LLP) > Constitution(Clause).
  //   setter(clauseOptionType 위임)와 동일 분류 → 탭 튕김 없음.
  if (/third\s+schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s) || /\bllp\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}
/** 선택된 Governing Document 라디오에 맞는 clause 옵션만 노출 (분류 불가 'other' 는 유지). */
const filteredClauseOptions = computed<string[]>(() =>
  articleNoOptions.value.filter((o) => {
    const ot = clauseOptionType(o);
    return ot === governingDocumentType.value || ot === 'other';
  })
);

/** 조항 전체 문자열에서 번호만 추출 (AOD parseClauseNo 와 동일 + paragraph/schedule 케이스). */
function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause|Paragraph)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association|schedule|paragraph/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

// v-combobox 진입점 — 번호만 저장 + 선택 옵션 전문에서 governing type 자동 감지 (사용자 라디오로 수정 가능).
const constitutionClauseNoModel = computed<string>({
  get: () => constitutionClauseNo.value,
  set: (val: string | null) => {
    const s = val || '';
    constitutionClauseNo.value = parseClauseNo(s);
    // 필터(clauseOptionType)와 동일 분류기로 라디오 자동 감지 — 탭 튕김 방지.
    const detected = clauseOptionType(s);
    if (detected !== 'other') governingDocumentType.value = detected;
  },
});
watch(
  () => icpCd.value,
  async (code) => {
    if (!code) return;
    entityInfoLoading.value = true;
    try {
      const [addrJson, profileJson] = await Promise.all([
        fetch(`/api/setupEntity/changeAddress/address-info/${code}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
        // articleNoOptions 전용 경량 엔드포인트 (메모리 캐시) — 무거운 전체 프로필 대신.
        fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
      ]);
      if (addrJson?.success) {
        if (!regNo.value) regNo.value = addrJson.regNo || '';
        if (!regNoOld.value && addrJson.regNoOld) regNoOld.value = String(addrJson.regNoOld);
        if (!constitutionClauseNo.value && addrJson.clauseNo) {
          // model setter 경유 — 번호만 저장 + governing type 자동 감지
          constitutionClauseNoModel.value = String(addrJson.clauseNo);
        }
      }
      // articleNoOptions 추출 — 전용 엔드포인트(top-level) 우선, 레거시 shape fallback.
      const opts =
        profileJson?.articleNoOptions ||
        profileJson?.data?.resolution?.articleNoOptions ||
        profileJson?.resolution?.articleNoOptions ||
        [];
      articleNoOptions.value = Array.isArray(opts)
        ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
        : [];
    } catch (err) {
      console.warn('[PreSetAR] entity info fetch failed:', err);
    } finally {
      entityInfoLoading.value = false;
    }
  },
  { immediate: true }
);

// ── AR Selector — SUBMITTED 만 ──
interface ArHistoryRow {
  AR_IDX: number;
  AR_TYPE: string;
  USE_SECTION_68_6: boolean | number | null;
  AR_DATE: string;
  FILING_YEAR: number;
  AR_STATUS: string;
  SUBMISSION_DATE?: string;
  SUBMISSION_REF_NO?: string;
}
const arList = ref<ArHistoryRow[]>([]);
const arListLoading = ref(false);
const selectedArIdx = ref<number | null>(null);
const selectedAr = computed<ArHistoryRow | null>(
  () => arList.value.find((r) => r.AR_IDX === selectedArIdx.value) || null
);
// AR1/AR4 판정 — AR_TYPE 컬럼은 'AR' 만 들어있을 수 있어서 USE_SECTION_68_6 플래그를 신뢰.
//   USE_SECTION_68_6 = 1 → AR4 (Section 68(6)), 0 → AR1.
const isAR4 = computed<boolean>(() => {
  const ar = selectedAr.value;
  if (!ar) return false;
  return !!ar.USE_SECTION_68_6;
});
const arCalendarYear = computed<string>(() => String(selectedAr.value?.FILING_YEAR || ''));

async function loadArList() {
  if (!icpCd.value) return;
  arListLoading.value = true;
  try {
    const res = await fetch(`/api/annual-return_my/history/${icpCd.value}`, {
      credentials: 'include',
    });
    const json = await res.json();
    const list: ArHistoryRow[] = (json?.data || []).filter(
      (r: ArHistoryRow) => String(r.AR_STATUS || '').toUpperCase() === 'SUBMITTED'
    );
    arList.value = list;
    // 자동 선택 — 가장 최근 1건.
    if (list.length > 0 && !selectedArIdx.value) {
      selectedArIdx.value = list[0].AR_IDX;
    }
  } catch (err) {
    console.warn('[PreSetAR] AR history fetch failed:', err);
  } finally {
    arListLoading.value = false;
  }
}

watch(() => icpCd.value, (cd) => { if (cd) void loadArList(); }, { immediate: true });

// ── DCR 폼 ──
//   resolution_date (필수) — useDateField 로 세션 dateFormat 적용.
// resolution_date — PreSetDateField(직접입력 + 초록 달력 + X skip). skip 시 blank lodge (AOD 동일).
const resolutionDate = ref('');
// X(skip) 대상 필드 집합.
const skippedFields = ref<Set<string>>(new Set());
function isFieldSkipped(field: string): boolean { return skippedFields.value.has(field); }
function toggleFieldSkip(field: string, skip: boolean) {
  const s = new Set(skippedFields.value);
  if (skip) s.add(field); else s.delete(field);
  skippedFields.value = s;
}

//   is_sole_director — auto/yes/no 3-way (selected AR 의 director count 로 추천).
type SoleOverride = 'auto' | 'yes' | 'no';
const soleOverride = ref<SoleOverride>('auto');
const directorCountFromAr = computed<number>(() => {
  // L_ANNUAL_RETURN.JSON_DATA 의 directors 배열 길이 추정 — selected AR 의 history 응답엔 미포함이므로 0.
  //   사용자 직접 override 옵션 제공. Phase 3 에서 GET /api/annual-return_my/:arIdx 호출로 정밀화 가능.
  return 0;
});
const recommendedSole = computed<'yes' | 'no'>(() => (directorCountFromAr.value === 1 ? 'yes' : 'no'));
const isSoleDirector = computed<boolean>(() =>
  soleOverride.value === 'auto' ? recommendedSole.value === 'yes' : soleOverride.value === 'yes'
);

// ── Doc Flags ──
const docFlags = computed(() => detectAnnualReturnDocFlags(props.selection?.documents || []));

// ── Report 옵션 — AR1 은 section68 + bo-sm 2건, AR4 는 section68 1건. ──
const generateReport = ref(true); // 기본 ON. AR1/AR4 에 따라 자동 분기.
const reportModes = computed<string[]>(() => {
  if (!generateReport.value) return [];
  return isAR4.value ? ['section68'] : ['section68', 'bo-sm'];
});
const reportLabels: Record<string, string> = {
  section68: 'Annual Return Report (Section 68)',
  'bo-sm': 'BO & SM Annexure',
};

// ── 검증 ──
const showValidationErrors = ref(false);
function hasFieldError(field: string): boolean {
  if (!showValidationErrors.value) return false;
  switch (field) {
    case 'selectedArIdx':
      return !selectedArIdx.value;
    case 'resolutionDate':
      if (isFieldSkipped('resolutionDate')) return false;
      return !String(resolutionDate.value || '').trim();
    case 'constitutionClauseNo':
      // Third Schedule 은 정관 없음(N/A) → clause 필수 검증 제외.
      if (governingDocumentType.value === 'third_schedule') return false;
      return !String(constitutionClauseNo.value || '').trim();
  }
  return false;
}
const isFormValid = computed<boolean>(() => {
  if (!selectedArIdx.value) return false;
  // Resolution Date 는 X(skip) 시 필수 아님(blank lodge).
  if (!isFieldSkipped('resolutionDate') && !String(resolutionDate.value || '').trim()) return false;
  // Third Schedule 은 clause N/A → 필수 아님.
  if (governingDocumentType.value !== 'third_schedule'
    && !String(constitutionClauseNo.value || '').trim()) return false;
  return true;
});

// ── Task config (taskFlow steps) ──
const taskConfigSteps = ref<string[]>([]);
onMounted(async () => {
  const taskCode = props.selection.task.TASK_CODE;
  if (taskCode) {
    try {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = ((cfg as any)?.processSteps as string[]) || [];
    } catch { /* ignore */ }
  }
});

// ── Drag & Drop (자리만 — DCR 은 input 필드 적음) ──
const isDragging = ref(false);
function _onGlobalDragStart() { isDragging.value = true; }
function _onGlobalDragEnd() { isDragging.value = false; }
onMounted(() => {
  document.addEventListener('dragstart', _onGlobalDragStart);
  document.addEventListener('dragend', _onGlobalDragEnd);
});
onUnmounted(() => {
  document.removeEventListener('dragstart', _onGlobalDragStart);
  document.removeEventListener('dragend', _onGlobalDragEnd);
});

// ── Create Task ──
const creating = ref(false);
const progress = ref('');

// 진행 상태는 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다.
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

async function handleCreateTask() {
  const title = props.taskTitle;
  if (!title) {
    progress.value = 'Error: Task Title is required — please enter it in the chat.';
    return;
  }
  if (!props.entityData) {
    progress.value = 'Error: Please select an entity in the chat first.';
    return;
  }
  if (!isFormValid.value) {
    showValidationErrors.value = true;
    progress.value = 'Error: Please fill all required fields.';
    return;
  }
  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) {
    progress.value = 'Error: Task Code not configured in PreSet.';
    return;
  }

  creating.value = true;
  progress.value = 'Creating task...';

  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };

    // PreSet 의 arIdx 등을 TASK_DATA.formData 에 동봉 — 후속 register/Report 흐름에서 참조.
    const presetMeta: Record<string, any> = {
      arIdx: selectedArIdx.value,
      arType: selectedAr.value?.AR_TYPE || '',
      filingYear: selectedAr.value?.FILING_YEAR || '',
      isAR4: isAR4.value,
      generateReport: generateReport.value,
      reportModes: reportModes.value,
    };

    // officerData → TASK_DATA.formData 로 저장. presetMeta(arIdx 등) + presetForm(Edit 복원 스냅샷).
    const officerData = {
      ...presetMeta,
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: title }),
        });
      } catch { /* title optional */ }
      progress.value = `Updating documents (Task ${taskId})...`;
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: icpCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: title,
          taskFlow,
          // AGM 에서는 별도 키 없이 호출하지만 AR 은 arIdx 가 핵심 — task data 에 포함시키도록.
          annualReturnData: presetMeta,
          officerData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
      progress.value = `Task created (ID: ${taskId}). Saving documents...`;
    }

    const commonFormData = buildAnnualReturnVariables({
      companyName: entityName.value,
      registrationNo: regNo.value,
      registrationNoOld: regNoOld.value,
      constitutionClauseNo: constitutionClauseNo.value,
      governingDocumentType: governingDocumentType.value,
      arCalendarYear: arCalendarYear.value,
      isSoleDirector: isSoleDirector.value,
      // X(skip) 시 blank 로 lodge.
      resolutionDate: isFieldSkipped('resolutionDate') ? '' : resolutionDate.value,
    });

    // ── DCR 문서 저장 ──
    const docsToSave = (props.selection.documents || [])
      .filter((d) => d.tmpId)
      .map((d) => ({
        templateId: d.tmpId,
        label: d.templateName,
        personName: entityName.value || '',
        formData: { ...commonFormData },
        entityCd: icpCd.value || undefined,
      }));

    if (docsToSave.length > 0) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) {
          progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
        }
      } catch (err: any) {
        console.warn('[PreSetAR] Document save failed:', err);
      }
    }

    // ── AR Report 첨부 (Phase 3 — 별도 helper 로 분리) ──
    if (generateReport.value && selectedArIdx.value) {
      progress.value = 'Generating AR Report(s)...';
      try {
        await attachArReportsToTask(taskId, selectedArIdx.value, reportModes.value);
      } catch (err: any) {
        console.warn('[PreSetAR] AR Report attach failed (continuing):', err);
        progress.value = `AR Report warning: ${err.message}`;
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch { /* ignore */ }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    try {
      progress.value = 'Preparing next screen...';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(
        `presetTaskDetailPrefetch:${taskId}`,
        JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }),
      );
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    console.error('[PreSetAR] create task failed:', err);
    progress.value = `Error: ${err.message}`;
  } finally {
    creating.value = false;
  }
}

/**
 * AR Report 첨부 — POST /api/annual-return_my/:arIdx/report/:mode/tsign  body: { signers:[], taskId }
 *   이 엔드포인트가 PDF 생성 + L_DOC_CUST_SAVE 에 TASK_ID 와 함께 저장.
 *   결과: 생성된 task 의 documents 목록에 AR Report PDF row 가 자동 추가됨 (T-Sign 단계 노출).
 *   Signers 는 사용자가 task detail 페이지에서 후속 선택 — 여기선 빈 배열로 저장하고 task 후속 step 에서 채움.
 */
async function attachArReportsToTask(taskId: number, arIdx: number, modes: string[]): Promise<void> {
  if (!modes || modes.length === 0) return;
  for (const mode of modes) {
    try {
      // format='pdf' — AR Report 는 Puppeteer HTML→PDF 가 원본 layout(테이블/CSS) 그대로 보존.
      //   DOCX 변환은 OnlyOffice 가 table 라인을 못 살려서 원본 양식과 달라짐.
      const res = await fetch(`/api/annual-return_my/${arIdx}/report/${mode}/tsign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ signers: [], taskId, format: 'pdf' }),
      });
      if (!res.ok) {
        console.warn('[PreSetAR] AR Report attach failed:', mode, res.status);
        continue;
      }
      const json = await res.json().catch(() => null);
      console.info('[PreSetAR] AR Report attached:', mode, {
        saveId: json?.data?.saveId,
        fileName: json?.data?.fileName,
        format: json?.data?.format,
      });
    } catch (err) {
      console.warn('[PreSetAR] AR Report fetch error:', mode, err);
    }
  }
}

// ── docs list display ──
const allDocsList = computed(() => {
  const docs = (props.selection.documents || []).map((d) => ({
    name: d.templateName || 'Document',
    type: 'preset' as const,
    skipped: false,
    reason: '',
  }));
  const reports = reportModes.value.map((mode) => ({
    name: reportLabels[mode] || mode,
    type: 'report' as const,
    skipped: false,
    reason: '',
  }));
  return [...docs, ...reports];
});

// ─── Edit 지원: 전체 입력 스냅샷 gather/restore (호스트가 TASK_DATA.formData.presetForm 로 저장/복원) ───
function gatherState(): Record<string, any> {
  return {
    regNo: regNo.value,
    regNoOld: regNoOld.value,
    constitutionClauseNo: constitutionClauseNo.value,
    governingDocumentType: governingDocumentType.value,
    selectedArIdx: selectedArIdx.value,
    resolutionDate: resolutionDate.value,
    skippedFields: Array.from(skippedFields.value),
    soleOverride: soleOverride.value,
    generateReport: generateReport.value,
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  if (s.regNo != null) regNo.value = s.regNo;
  if (s.regNoOld != null) regNoOld.value = s.regNoOld;
  if (s.constitutionClauseNo != null) constitutionClauseNo.value = s.constitutionClauseNo;
  if (s.governingDocumentType != null) governingDocumentType.value = s.governingDocumentType;
  if (s.selectedArIdx != null) selectedArIdx.value = Number(s.selectedArIdx) || null;
  if (s.resolutionDate != null) resolutionDate.value = s.resolutionDate;
  if (Array.isArray(s.skippedFields)) skippedFields.value = new Set(s.skippedFields);
  if (s.soleOverride != null) soleOverride.value = s.soleOverride;
  if (typeof s.generateReport === 'boolean') generateReport.value = s.generateReport;
  // 파생 watcher 가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="ar-panel">
    <!-- Header -->
    <div class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Annual Return</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>Back
      </v-btn>
    </div>

    <!-- Task Title -->
    <div class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:tag-outline" width="14" color="#888" />
        <span>Task Title</span>
      </div>
      <div class="task-title-box">
        <span>{{ taskTitle || '—' }}</span>
        <v-btn size="x-small" variant="tonal" color="primary" @click="emit('change-task-title')">
          Change
        </v-btn>
      </div>
    </div>

    <!-- Entity 카드 -->
    <div class="co-section">
      <div v-if="icpCd" class="entity-card">
        <Icon icon="mdi:domain" width="22" color="#3b82f6" />
        <div class="entity-card-info">
          <div class="entity-card-name">{{ entityName || icpCd }}</div>
          <div class="entity-card-meta">
            <span>Registration No: <strong>{{ regNo || '—' }}</strong></span>
            <span v-if="constitutionClauseNo" class="ml-2">
              · Clause: <strong>{{ constitutionClauseNo }}</strong>
            </span>
            <span v-if="entityInfoLoading" class="ml-2 text-grey">loading...</span>
          </div>
        </div>
        <v-btn size="x-small" variant="tonal" color="primary" @click="emit('change-entity')">
          Change
        </v-btn>
      </div>
      <div v-else class="co-empty">
        <Icon icon="mdi:domain" width="18" color="#888" />
        <span>Select an entity in the chat to begin.</span>
      </div>
    </div>

    <!-- AR Selector -->
    <div v-if="icpCd" class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:clipboard-list-outline" width="14" color="#888" />
        <span>Annual Return (Submitted)</span>
        <v-spacer />
        <v-btn
          size="x-small"
          variant="text"
          :loading="arListLoading"
          @click="loadArList"
        >
          <Icon icon="mdi:refresh" width="14" />
          Refresh
        </v-btn>
      </div>
      <div v-if="arListLoading && arList.length === 0" class="co-empty">
        <v-progress-circular indeterminate size="18" />
        <span>Loading submitted Annual Returns...</span>
      </div>
      <div v-else-if="arList.length === 0" class="co-empty">
        <Icon icon="mdi:alert-circle-outline" width="18" color="#f59e0b" />
        <span>No submitted Annual Return found for this entity.</span>
      </div>
      <div v-else class="ar-list">
        <label
          v-for="ar in arList"
          :key="ar.AR_IDX"
          class="ar-row"
          :class="{ selected: selectedArIdx === ar.AR_IDX }"
        >
          <input
            type="radio"
            :value="ar.AR_IDX"
            v-model="selectedArIdx"
          />
          <div class="ar-row-info">
            <div class="ar-row-title">
              <strong>{{ ar.FILING_YEAR }}</strong>
              <v-chip
                size="x-small"
                variant="tonal"
                :color="ar.USE_SECTION_68_6 ? 'warning' : 'primary'"
                class="ml-2"
              >
                {{ ar.USE_SECTION_68_6 ? 'AR4' : 'AR1' }}
              </v-chip>
              <v-chip size="x-small" variant="tonal" color="success" class="ml-1">
                {{ ar.AR_STATUS }}
              </v-chip>
            </div>
            <div class="ar-row-meta">
              <span v-if="ar.SUBMISSION_DATE">Submitted: {{ String(ar.SUBMISSION_DATE).slice(0, 10) }}</span>
              <span v-if="ar.SUBMISSION_REF_NO" class="ml-2">· Ref: {{ ar.SUBMISSION_REF_NO }}</span>
            </div>
          </div>
        </label>
      </div>
      <div
        v-if="hasFieldError('selectedArIdx')"
        class="text-caption text-error mt-1"
      >
        Please select a submitted Annual Return.
      </div>
    </div>

    <!-- DCR 폼 (selected AR 가 있어야 활성화) -->
    <div v-if="icpCd && docFlags.hasDcr" class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:file-document-edit-outline" width="14" color="#888" />
        <span>Directors' Circular Resolution (DCR)</span>
        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2">
          Lodgement of Annual Return
        </v-chip>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <!-- Third Schedule: 정관 없음 → clause N/A (disabled, 필수 아님) -->
          <v-text-field
            v-if="governingDocumentType === 'third_schedule'"
            model-value="N/A"
            disabled
            label="Constitution Clause No"
            variant="outlined"
            density="compact"
            hide-details="auto"
          />
          <!-- Constitution Clause: 자동 fetch + L_ARTICLE_NO 마스터 후보.
               combobox = free typing 허용. 선택/입력 텍스트 전체 저장. -->
          <v-combobox
            v-else
            v-model="constitutionClauseNoModel"
            :items="filteredClauseOptions"
            :label="(governingDocumentType === 'articles' ? 'Article No' : 'Constitution Clause No') + ' *'"
            placeholder="Select or type clause number"
            variant="outlined"
            density="compact"
            hide-details="auto"
            clearable
            :error="hasFieldError('constitutionClauseNo')"
            :error-messages="hasFieldError('constitutionClauseNo') ? ['Required'] : []"
            :hint="
              filteredClauseOptions.length > 0
                ? `${filteredClauseOptions.length} option(s) — only the clause number is stored`
                : 'No constitution clauses found — type manually'
            "
            persistent-hint
          />
        </v-col>
        <!-- Governing Document Type — DCR 의 [[if constitution]] / [[if articles]] / [[if third_schedule]] 분기 (AOD 동일) -->
        <v-col cols="12">
          <div class="text-caption text-grey-darken-1 mb-1">
            Governing Document <span class="text-error">*</span>
            <span class="text-grey-darken-1 ml-2">— Resolution refers to this clause source</span>
          </div>
          <v-radio-group
            v-model="governingDocumentType"
            @update:model-value="constitutionClauseNo = ''"
            hide-details
            inline
            density="compact"
            class="mt-0"
          >
            <v-radio label="Constitution" value="constitution" />
            <v-radio label="Articles of Association" value="articles" />
            <v-radio label="Third Schedule (Companies Act 2016)" value="third_schedule" />
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="6">
          <!-- Resolution Date — 직접입력 + 초록 달력 + X(skip). skip 시 blank lodge (AOD 동일) -->
          <PreSetDateField
            v-model="resolutionDate"
            label="Resolution Date *"
            skippable
            :skipped="isFieldSkipped('resolutionDate')"
            @update:skipped="(v) => toggleFieldSkip('resolutionDate', v)"
          />
          <div v-if="hasFieldError('resolutionDate')" class="text-caption text-error mt-1">
            Required
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :model-value="arCalendarYear"
            label="AR Calendar Year"
            variant="outlined"
            density="compact"
            hide-details
            readonly
            placeholder="(Select an AR above)"
          />
        </v-col>
        <v-col cols="12" md="6">
          <div class="sole-director-block">
            <div class="text-caption mb-1">Sole Director?</div>
            <v-btn-toggle
              v-model="soleOverride"
              mandatory
              density="compact"
              divided
            >
              <v-btn value="auto" size="small">
                Auto
                <span v-if="recommendedSole" class="text-caption text-grey ml-1">
                  ({{ recommendedSole === 'yes' ? 'Yes' : 'No' }})
                </span>
              </v-btn>
              <v-btn value="yes" size="small">Yes</v-btn>
              <v-btn value="no" size="small">No</v-btn>
            </v-btn-toggle>
            <div class="text-caption text-grey-darken-1 mt-1">
              Branches `[[if is_sole_director]]` in DCR template.
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Report 옵션 -->
    <div v-if="icpCd && selectedArIdx" class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:file-chart-outline" width="14" color="#888" />
        <span>Annual Return Report</span>
      </div>
      <v-checkbox
        v-model="generateReport"
        color="primary"
        density="compact"
        hide-details
        :label="`Generate AR Report${isAR4 ? '' : ' (incl. BO/SM Annexure)'}`"
      />
      <div v-if="generateReport" class="text-caption text-grey-darken-1 mt-1">
        <span v-if="isAR4">
          AR4 — Section 68 report only.
        </span>
        <span v-else>
          AR1 — Section 68 report + BO/SM Annexure.
        </span>
      </div>
    </div>

    <!-- Documents to be generated -->
    <div v-if="icpCd && allDocsList.length > 0" class="co-section">
      <div class="co-section-header">
        <Icon icon="mdi:file-multiple-outline" width="14" color="#888" />
        <span>Documents to be generated</span>
      </div>
      <div class="doc-list">
        <div v-for="(d, i) in allDocsList" :key="i" class="doc-list-item">
          <v-icon size="14" color="success">mdi-check-circle</v-icon>
          <span>{{ d.name }}</span>
          <v-chip
            size="x-small"
            variant="tonal"
            :color="d.type === 'report' ? 'warning' : 'primary'"
            class="ml-2"
          >
            {{ d.type === 'report' ? 'Report' : 'Template' }}
          </v-chip>
        </div>
      </div>
    </div>

    <!-- Progress / Footer -->
    <div v-if="progress" class="progress-line text-caption" :class="{ 'text-error': progress.startsWith('Error:') }">
      {{ progress }}
    </div>

    <div class="co-footer">
      <v-btn variant="outlined" @click="emit('back')">Back</v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              color="primary"
              variant="flat"
              :loading="creating"
              :disabled="creating || (editMode && !isDirty)"
              @click="handleCreateTask"
            >
              <v-icon start size="16">{{ editMode ? 'mdi-content-save' : 'mdi-check' }}</v-icon>
              {{ editMode ? 'Update Task' : 'Create Task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>
  </div>
</template>

<style scoped>
.ar-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px 16px 16px;
  gap: 12px;
  background: #fafbfd;
  overflow-y: auto;
}

.co-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}
.co-title {
  font-size: 17px;
  font-weight: 700;
  color: #1f2937;
}
.co-subtitle {
  font-size: 12px;
  color: #6b7280;
}

.co-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
}
.co-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-radius: 6px;
  padding: 6px 8px;
}
.task-title-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  color: #334155;
}

.entity-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f0f5ff;
  border: 1px solid #c7d6ff;
  border-radius: 8px;
}
.entity-card-info {
  flex: 1;
}
.entity-card-name {
  font-size: 14px;
  font-weight: 700;
  color: #1e3a8a;
}
.entity-card-meta {
  font-size: 12px;
  color: #475569;
}

.co-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  color: #64748b;
  justify-content: center;
}

.ar-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
}
.ar-row:hover {
  background: #f9fafb;
}
.ar-row.selected {
  background: #eef2ff;
  border-color: #6366f1;
}
.ar-row-info {
  flex: 1;
}
.ar-row-title {
  font-size: 13px;
  display: flex;
  align-items: center;
}
.ar-row-meta {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.sole-director-block {
  padding: 4px 0;
}

.doc-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.doc-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 6px 10px;
  font-size: 12px;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.doc-list-item > span {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.progress-line {
  padding: 6px 10px;
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  color: #92400e;
}

.co-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  margin-left: -16px;
  margin-right: -16px;
  margin-top: auto;
  z-index: 2;
}
</style>
